/**
 * An order the operator arranged by hand, and the way back to the automatic one.
 *
 * Three screens now let rows be dragged into an order — the session list, the
 * fleet and the account pool — and each has an automatic order it replaces:
 * waiting-first, a sorted column, the server's own. One implementation, because
 * the rule is the same everywhere and CLAUDE.md is explicit about it: **a prefill
 * may fill a field nobody has set; it may never overwrite one a human has set.**
 * An automatic order is a prefill — a useful guess about what matters — and a
 * dragged one is a decision.
 *
 * The decision therefore latches. Nothing re-sorts behind it, not a poll, not a
 * socket delta, not a refetch; only `reset` gives the order back, and that is a
 * control the screen has to show, because a decision needs a visible way out from
 * where it was taken.
 *
 * Rows the stored order has never seen — started, enrolled or signed in since —
 * sort *after* the ones it has, rather than vanishing or jumping to the top. A
 * hand order is a statement about the rows that existed when it was made, and it
 * says nothing about the rest.
 */

import { useCallback, useMemo, useState } from 'react'

import { useT } from '@/i18n/context'

export interface ManualOrder {
  /** The stored order, or null while the automatic one is still in charge. */
  order: string[] | null
  /** Sorts rows into the operator's order. A no-op while `order` is null. */
  apply: <T extends { id: string }>(rows: readonly T[]) => T[]
  /** Latches: after this the automatic order no longer applies. */
  set: (ids: string[]) => void
  /** Gives the order back to whatever produced it before. */
  reset: () => void
}

/**
 * Sorts by a stored order, stably.
 *
 * Exported for the tests and for callers that hold an order from somewhere other
 * than this hook. Stable within the unknown tail as well as the known head, so a
 * screen whose automatic order still means something keeps it for the rows the
 * hand order does not mention.
 */
export function applyOrder<T extends { id: string }>(rows: readonly T[], order: string[] | null): T[] {
  if (!order) return [...rows]
  const rank = new Map(order.map((id, i) => [id, i]))
  return rows
    .map((row, i) => ({ row, i, at: rank.get(row.id) ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => a.at - b.at || a.i - b.i)
    .map((x) => x.row)
}

function read(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key)
    if (raw == null) return null
    const parsed: unknown = JSON.parse(raw)
    // Written by a person, or by another version of this code. An unreadable
    // preference is not an error to report — it is a preference to ignore.
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : null
  } catch {
    return null
  }
}

export function useManualOrder(storageKey: string): ManualOrder {
  const [order, setOrder] = useState<string[] | null>(() => read(storageKey))

  const set = useCallback(
    (ids: string[]) => {
      setOrder(ids)
      try {
        localStorage.setItem(storageKey, JSON.stringify(ids))
      } catch {
        // A full or blocked store must not take the screen down. The order holds
        // for this tab; it just will not outlive it.
      }
    },
    [storageKey],
  )

  const reset = useCallback(() => {
    setOrder(null)
    try {
      localStorage.removeItem(storageKey)
    } catch {
      // As above.
    }
  }, [storageKey])

  const apply = useCallback(
    <T extends { id: string }>(rows: readonly T[]) => applyOrder(rows, order),
    [order],
  )

  return { order, apply, set, reset }
}

/**
 * What a screen reader is told while a row is being dragged.
 *
 * dnd-kit ships defaults, and they are English and they name ids: "Draggable item
 * 019fe70c-801b-… was moved over droppable area 019fe70c-…". In a console whose
 * source language is Turkish and whose whole point is that an operator can read
 * what is happening, that is not an announcement, it is a leak. So all three
 * reorderable screens pass this instead — one wording, in one place, for the same
 * reason `order.move` is shared.
 *
 * @param nameOf turns an id into the name the operator knows it by.
 */
export function useDragAnnouncements(nameOf: (id: string) => string) {
  const t = useT()
  return useMemo(
    () => ({
      onDragStart: ({ active }: { active: { id: string | number } }) =>
        t('order.dragStart', { name: nameOf(String(active.id)) }),
      onDragOver: ({ over }: { over: { id: string | number } | null }) =>
        over ? t('order.dragOver', { name: nameOf(String(over.id)) }) : undefined,
      onDragEnd: ({ over }: { over: { id: string | number } | null }) =>
        over ? t('order.dragEnd', { name: nameOf(String(over.id)) }) : t('order.dragCancel'),
      onDragCancel: () => t('order.dragCancel'),
    }),
    [t, nameOf],
  )
}

/**
 * Sortable identity, scoped to the surface it is on.
 *
 * The list and the wall both sort sessions, and dnd-kit keys its registry of
 * draggables and droppables by id — so an unprefixed session id made a row and its
 * tile the same registered thing. The symptom was silent and complete: every drop
 * resolved to itself, the announcements read correctly, and nothing ever moved.
 */
export type DragSurface = 'list' | 'wall'

export function dragId(surface: DragSurface, id: string): string {
  return `${surface}:${id}`
}

export function parseDragId(raw: string): { surface: DragSurface; id: string } {
  const at = raw.indexOf(':')
  const surface = raw.slice(0, at)
  return {
    surface: surface === 'wall' ? 'wall' : 'list',
    id: at < 0 ? raw : raw.slice(at + 1),
  }
}
